import moment from "moment";
import "moment/locale/fr";
import { DataChartType } from "../../Hooks/Dashboard/useHome";
import { isVerifiedTrainer } from "../../Utils/VerifiedTrainerAmounts";
import { courseSessions } from "../CoursesV2/courseRows";

/**
 * Lecture des données du tableau de bord repensé (maquette 7a) : mois du
 * graphique des revenus, séances à venir. Mêmes champs et mêmes formules que
 * l'onglet « Vue globale » actuel (`AllBordTab`), sans règle nouvelle.
 */

export type ChartMonth = NonNullable<DataChartType["courses"]>[number];

/** Rémunération enregistrée du mois (part HT + TVA), à défaut le net. */
export const monthRemuneration = (c: ChartMonth | undefined): number => parseFloat(c?.remuneration ?? c?.teachr_price ?? "0") || 0;
const monthNet = (c: ChartMonth | undefined): number => parseFloat(c?.teachr_price ?? "0") || 0;
const monthFees = (c: ChartMonth | undefined): number => parseFloat(c?.fees ?? "0") || 0;
/** Montant mis en avant : prix total TTC chez Verified Trainer (net + commissions), rémunération ailleurs. */
export const monthRevenue = (c: ChartMonth | undefined): number => (isVerifiedTrainer() ? monthNet(c) + monthFees(c) : monthRemuneration(c));
/** Durée du mois en demi-heures, comme l'API. */
export const monthHalfHours = (c: ChartMonth | undefined): number => parseFloat(c?.duration ?? "0") || 0;

/** « Nov 2026 » (to_char TMMon YYYY) → moment du premier jour du mois, en français puis en anglais. */
export const parseChartMonth = (label: string | undefined): moment.Moment | null => {
    if (!label) return null;
    const cleaned = label.replace(".", "").trim();
    const fr = moment(cleaned, "MMM YYYY", "fr", true);
    if (fr.isValid()) return fr.startOf("month");
    const en = moment(cleaned, "MMM YYYY", "en", true);
    if (en.isValid()) return en.startOf("month");
    const loose = moment(cleaned, "MMM YYYY", "fr");
    return loose.isValid() ? loose.startOf("month") : null;
};

/** Mois du graphique correspondant à une date, s'il existe. */
export const findChartMonth = (data: DataChartType | undefined, date: moment.MomentInput): ChartMonth | undefined =>
    (data?.courses ?? []).find((c) => parseChartMonth(c.startmonth)?.isSame(moment(date), "month"));

/** Total mis en avant sur la période du graphique (même calcul que « Tendance des revenus »). */
export const periodRevenue = (data: DataChartType | undefined): number | null => {
    if (!data) return null;
    if (isVerifiedTrainer() && data.totalAmount != null) return Number(data.totalAmount) + Number(data.fees ?? 0);
    const value = data.remuneration ?? data.totalAmount;
    return value != null ? Number(value) : null;
};

/** Total depuis le début (données « startTeach »). */
export const lifetimeRevenue = (data: DataChartType | undefined): number | null => {
    if (!data) return null;
    if (isVerifiedTrainer()) return (data.total ?? 0) + (data.totalFees ?? 0);
    const value = data.totalRemuneration ?? data.total;
    return value != null ? Number(value) : null;
};

export interface UpcomingSession {
    key: string;
    course: any;
    start: moment.Moment;
    end: moment.Moment;
    minutes: number;
}

/** Prochaines séances des cours à venir, triées, à partir de maintenant. */
export const upcomingSessions = (pendingRows: any[], limit = 3, from: moment.Moment = moment()): UpcomingSession[] =>
    pendingRows
        .flatMap((course) => courseSessions(course).map((s, i) => ({
            key: `${course?.id ?? "c"}-${i}`,
            course,
            start: s.date,
            end: s.date.clone().add(s.minutes, "minute"),
            minutes: s.minutes,
        })))
        .filter((s) => s.end.isAfter(from))
        .sort((a, b) => a.start.valueOf() - b.start.valueOf())
        .slice(0, limit);

/** Nombre de séances à venir d'ici la fin de la semaine (dimanche inclus). */
export const sessionsThisWeek = (pendingRows: any[], now: moment.Moment = moment()): number => {
    const end = now.clone().endOf("isoWeek");
    return pendingRows
        .flatMap((course) => courseSessions(course))
        .filter((s) => s.date.isSameOrAfter(now, "minute") && s.date.isSameOrBefore(end)).length;
};
